<script setup lang="ts">
import { reactive, onMounted, computed } from 'vue';
import {
  useEmployeeWithTimesheetsStore,
  ComingUpStatus,
} from '@/stores/employeesWithTimesheets/employeesWithTimesheets';
import type { ComingUpData } from '@/lib/request/types/employeesWithTimesheets';
import EmployeeListItem from './EmployeeListItem.vue';
import SearchErrorImage from '../assets/images/search-error.png';
import { getTranslations } from '@/lib/util/i18n';
import SelectListSearch from '@/components/SelectListSearch.vue';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const employeesWithTimesheets = useEmployeeWithTimesheetsStore();
const emit = defineEmits(['select']);

const state = reactive({
  searchTerm: '',
  isReady: false,
});

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'employee-list',
});

const selectEmployee = (id: number) => {
  emit('select', id);
};

const ITEM_SIZE = 90; // in pixels the size of each list item;
const VIRTUAL_BUFFER = 1000; // in pixels the amount of additional screen beyond the viewport to render elements.

function search(searchTerm: string) {
  state.searchTerm = searchTerm;
}

onMounted(async () => {
  state.isReady = true;
});

const searchUserMatches = computed(() =>
  employeesWithTimesheets.searchUsers(state.searchTerm || '')
);

function getStatusMinutes(statusData: ComingUpData) {
  return statusData.minutes;
}

function getComingUpLabel(id: number) {
  const employeeWithTimesheets = useEmployeeWithTimesheetsStore();
  const statusData = employeeWithTimesheets.getComingUpDataByEmployeeId(id);
  const statusTranslationsMap: any = {
    [ComingUpStatus.Late]: 'late',
    [ComingUpStatus.StartingSoon]: 'starting-soon',
    [ComingUpStatus.BreakLate]: 'break-late',
    [ComingUpStatus.BreakStartingSoon]: 'break-starting-soon',
    [ComingUpStatus.BreakOver]: 'break-over',
    [ComingUpStatus.OnBreak]: 'on-break',
    [ComingUpStatus.BreakEndingSoon]: 'break-ending-soon',
    [ComingUpStatus.EndingSoon]: 'ending-soon',
    [ComingUpStatus.OverTime]: 'over-time',
  };

  if (!statusData?.status) {
    return;
  }

  const { status } = statusData;

  const minutes = getStatusMinutes(statusData);

  return t(`coming-up-label:${statusTranslationsMap[status]}`, {
    count: minutes,
  });
}

function getStartEndLabel(id: number) {
  return employeesWithTimesheets.getStartEndTimeLabel(id);
}

const employeeGroups = computed(() => [
  {
    header: t('coming-up'),
    data: employeesWithTimesheets.employeesComingUp,
  },
  {
    header: t('on-shift'),
    data: employeesWithTimesheets.employeesOnShift,
  },
  {
    header: t('all-others'),
    data: employeesWithTimesheets.employeesNoStatus,
  },
]);
</script>

<template>
  <Teleport to="#navbar-teleported-content" v-if="state.isReady">
    <SelectListSearch
      class="__search-input"
      :placeholder="t('search-placeholder')"
      @search="search"
    ></SelectListSearch>
  </Teleport>

  <div v-show="!state.searchTerm">
    <div v-for="(group, index) in employeeGroups" :key="index">
      <div v-show="group.data.length" class="__employee-list">
        <h3
          v-show="
            employeeGroups[0].data.length || employeeGroups[1].data.length
          "
        >
          {{ group.header }}
        </h3>
        <RecycleScroller
          :items="group.data"
          :item-size="ITEM_SIZE"
          page-mode
          class="scroller"
          listClass=""
          listTag="ul"
          itemTag="li"
          key-field="employeeId"
          :buffer="VIRTUAL_BUFFER"
        >
          <template v-slot="{ item }">
            <EmployeeListItem
              data-test-id="employee-coming-up"
              :key="item.employee.Id"
              :data-test-employee-id="item.employee.Id"
              @select="selectEmployee(item.employee.Id)"
              :employee="item.employee"
              :photo="item.employee.Photo"
              :displayName="item.employee.DisplayName"
              :shiftLabel="getStartEndLabel(item.employee.Id)"
              :comingUpLabel="getComingUpLabel(item.employee.Id)"
            />
          </template>
        </RecycleScroller>
      </div>
    </div>
  </div>
  <div v-show="state.searchTerm">
    <ul class="__employee-list">
      <li
        v-for="employeeWithTimesheet in searchUserMatches"
        :key="employeeWithTimesheet.employee.Id"
      >
        <EmployeeListItem
          data-test-id="employee"
          @select="selectEmployee(employeeWithTimesheet.employee.Id)"
          :employee="employeeWithTimesheet.employee"
          :photo="employeeWithTimesheet.employee.Photo"
          :displayName="employeeWithTimesheet.employee.DisplayName"
          :timesheets="employeeWithTimesheet.timesheets"
          :rosters="employeeWithTimesheet.rosters"
          :shiftLabel="getStartEndLabel(employeeWithTimesheet.employee.Id)"
        />
      </li>
    </ul>
  </div>
  <div class="__empty-employee-list" v-show="!searchUserMatches.length">
    <img :src="SearchErrorImage" />
    <p>{{ t('no-results-found', { searchTerm: state.searchTerm }) }}</p>
  </div>
</template>
<style scoped>
h3 {
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 16px;
  line-height: 22px;
}
.__employee-list {
  list-style: none;
  padding: 0;
  margin-bottom: 32px;
}

.vue-recycle-scroller__item-view {
  margin-bottom: 16px;
}

.__empty-employee-list img {
  margin: 0 auto 15px;
}

.__empty-employee-list {
  text-align: center;
  font-size: 17px;
  color: var(--co-button-disabled-text);
}

.__search-input {
  display: block;
  font-size: 17px;
  background: url(../assets/images/search.svg) no-repeat scroll 8px 8px,
    var(--co-background-secondary);
  height: 40px;
  border-radius: 8px;
  line-height: 40px;
  border: none;
  padding: 0 15px 0 41px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
}
</style>
